import React, { useMemo } from "react";
import {
  Box,
  Typography,
  Divider,
  TextField,
  Grid,
  Button,
  Checkbox,
} from "@mui/material";

import {
  GradingDetailsGroupDTO,
  ParticipantGradingDTO,
  GradingDetailsDTO,
  ParticipantGradingRemarkDTO,
} from "../types/ExerciseGrading";

import { TeamDTO } from "../types/teamMembers";
import { EventPhase } from "../enums/EventsPhaseEnum";

const FONT_STYLE = { fontFamily: "Verdana", fontSize: "13px" };

interface Props {
  groups: GradingDetailsGroupDTO[];
  phase: EventPhase;
  results: ParticipantGradingDTO[];
  allRemarks: ParticipantGradingRemarkDTO[];
  onChange: React.Dispatch<React.SetStateAction<ParticipantGradingDTO[]>>;
  handleSaveInterview: (
    remarksByGroup: Record<number, string>,
  ) => Promise<void>;
}

const InterviewPanel: React.FC<Props> = ({
  groups,
  phase,
  results,
  allRemarks,
  onChange,
  handleSaveInterview,
}) => {
  const [editingKey, setEditingKey] = React.useState<string | null>(null);
  const [editValue, setEditValue] = React.useState("");

  const [remarksByGroup, setRemarksByGroup] = React.useState<
    Record<number, string>
  >({});

  type ParticipantGradingUpdate = Partial<
    Omit<
      ParticipantGradingDTO,
      "gradingDetailsID" | "gradingDetailsGroupID" | "participantID"
    >
  >;

  React.useEffect(() => {
    if (allRemarks.length === 0) return;

    const groupIdSet = new Set(groups.map((g) => g.id));

    const mapped: Record<number, string> = {};
    allRemarks
      .filter(
        (r) =>
          r.teamMemberID === null && groupIdSet.has(r.gradingDetailsGroupID),
      )
      .forEach((r) => {
        mapped[r.gradingDetailsGroupID] = r.remark ?? "";
      });

    setRemarksByGroup(mapped);
  }, [allRemarks, groups]);

  function updateTeamResult(
    gradingDetailsID: number,
    teamId: number | null,
    updates: ParticipantGradingUpdate,
  ) {
    onChange((prev) =>
      prev.map((r) =>
        r.gradingDetailsID === gradingDetailsID &&
        (teamId === null ? !r.team : r.team?.id === teamId)
          ? { ...r, ...updates, hasGradingByTeamMember: true }
          : r,
      ),
    );
  }

  const byDetail = useMemo(() => {
    const map: Record<number, ParticipantGradingDTO[]> = {};
    results.forEach((r) => {
      map[r.gradingDetailsID] ??= [];
      map[r.gradingDetailsID].push(r);
    });
    return map;
  }, [results]);

  const maxPts = useMemo(() => {
    const m: Record<number, number> = {};
    groups.forEach((g) =>
      g.gradingDetails.forEach((d) => (m[d.id] = d.points)),
    );
    return m;
  }, [groups]);

  const totalsByGroup = useMemo(() => {
    return groups.map((g) => {
      const teamMap = new Map<number, TeamDTO>();
      let hasUnassigned = false;

      g.gradingDetails.forEach((d) => {
        const all = byDetail[d.id] ?? [];
        all.forEach((r) => {
          if (r.team) teamMap.set(r.team.id, r.team);
          else hasUnassigned = true;
        });
      });

      const groupTeams: (TeamDTO | null)[] = [
        ...Array.from(teamMap.values()),
        ...(hasUnassigned ? [null] : []),
      ];

      return groupTeams.map((t) => {
        let sum = 0;

        g.gradingDetails.forEach((d) => {
          const all = byDetail[d.id] ?? [];

          const r = t
            ? all.find((x) => x.team?.id === t.id)
            : all.find((x) => !x.team);

          if (r?.checked) {
            sum += r.points ?? 0;
          }
        });

        return sum * (g.averageFactor ?? 1);
      });
    });
  }, [groups, byDetail]);

  const commitEdit = (
    value: string,
    d: GradingDetailsDTO,
    t: TeamDTO | null,
    otherTeam: ParticipantGradingDTO | null | undefined,
  ) => {
    const raw = parseFloat(value.replace(",", "."));

    const valueNum =
      !isNaN(raw) && raw >= 0 ? Math.min(raw, maxPts[d.id] ?? raw) : 0;

    const correction =
      valueNum != null &&
      otherTeam?.points != null &&
      valueNum !== otherTeam.points;

    updateTeamResult(d.id, t ? t.id : null, {
      points: valueNum,
      checked: true,
      correction: correction,
    });

    setEditingKey(null);
  };

  const toggleCheck = (
    r: ParticipantGradingDTO | undefined,
    d: GradingDetailsDTO,
    t: TeamDTO | null,
  ) => {
    if (!r) return;

    const newChecked = !r.checked;

    updateTeamResult(d.id, t ? t.id : null, {
      checked: newChecked,
      points: newChecked ? d.points : 0,
    });
  };

  const allReadonly = useMemo(() => {
    return groups.every((g) =>
      g.gradingDetails.every((d) =>
        (byDetail[d.id] ?? []).every((r) => r.readonly),
      ),
    );
  }, [groups, byDetail]);

  return (
    <Box sx={{ overflowY: "auto", pr: 2, mt: 2 }}>
      {groups
        .slice()
        .sort((a, b) => a.index - b.index)
        .map((group, gi) => {
          const teamMap = new Map<number, TeamDTO>();
          let hasUnassigned = false;

          group.gradingDetails.forEach((d) => {
            const all = byDetail[d.id] ?? [];
            all.forEach((r) => {
              if (r.team) teamMap.set(r.team.id, r.team);
              else hasUnassigned = true;
            });
          });

          const groupTeams: (TeamDTO | null)[] = [
            ...Array.from(teamMap.values()),
            ...(hasUnassigned ? [null] : []),
          ];

          return (
            <Box key={group.id} mb={3}>
              <Typography sx={{ fontWeight: 600, mb: 1, ...FONT_STYLE }}>
                {group.name}
              </Typography>

              <Grid container alignItems="center" sx={{ mb: 1 }}>
                <Grid item sx={{ flex: 1 }} />

                {groupTeams.map((t, idx) => (
                  <Grid
                    item
                    key={t ? t.id : `unassigned-${idx}`}
                    sx={{ width: 90, textAlign: "center", ml: 1 }}
                  >
                    {t && (
                      <img
                        alt={t.name}
                        src={`https://ui-avatars.com/api/?name=${encodeURIComponent(
                          t.name,
                        )}&background=0D8ABC&color=fff`}
                        style={{
                          width: 36,
                          height: 36,
                          borderRadius: "50%",
                        }}
                      />
                    )}
                  </Grid>
                ))}
              </Grid>

              {group.gradingDetails
                .slice()
                .sort((a, b) => a.index - b.index)
                .map((d) => {
                  const all = byDetail[d.id] ?? [];

                  return (
                    <Grid
                      container
                      alignItems="center"
                      sx={{ py: 0.5, px: 0.5 }}
                      key={d.id}
                    >
                      <Grid item sx={{ flex: 1 }}>
                        <Typography sx={FONT_STYLE}>{d.text}</Typography>
                      </Grid>

                      {groupTeams.map((t) => {
                        const r = t
                          ? all.find((x) => x.team?.id === t.id)
                          : all.find((x) => !x.team);

                        const otherTeam = t
                          ? all.find((x) => x.team?.id !== t.id)
                          : null;

                        const key = `${d.id}-${t ? t.id : "unassigned"}`;

                        return (
                          <Grid
                            item
                            key={key}
                            sx={{ width: 90, textAlign: "center", ml: 1 }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                gap: 0.5,
                              }}
                            >
                              {phase === EventPhase.Interview && (
                                <Checkbox
                                  size="small"
                                  checked={r?.checked ?? false}
                                  disabled={!r || r.readonly}
                                  onChange={() => toggleCheck(r, d, t)}
                                  sx={{ p: 0 }}
                                />
                              )}

                              <TextField
                                size="small"
                                disabled={
                                  !r ||
                                  r.readonly ||
                                  (phase === EventPhase.Interview && !r.checked)
                                }
                                value={
                                  editingKey === key
                                    ? editValue
                                    : r?.checked
                                      ? r?.points?.toFixed(2)
                                      : ""
                                }
                                onFocus={() => {
                                  if (!r) return;
                                  if (
                                    phase === EventPhase.Interview &&
                                    !r.checked
                                  )
                                    return;

                                  setEditingKey(key);
                                  setEditValue(r.points.toFixed(2));
                                }}
                                onChange={(e) => setEditValue(e.target.value)}
                                onBlur={() =>
                                  r &&
                                  (phase !== EventPhase.Interview ||
                                    r.checked) &&
                                  commitEdit(editValue, d, t, otherTeam)
                                }
                                onKeyDown={(e) => {
                                  if (e.key === "Enter") {
                                    (e.target as HTMLInputElement).blur();
                                  }
                                }}
                                inputProps={{ style: FONT_STYLE }}
                                sx={{
                                  width: 90,
                                  "& .MuiOutlinedInput-notchedOutline": {
                                    borderColor: r?.correction
                                      ? "red !important"
                                      : undefined,
                                  },
                                }}
                              />
                            </Box>
                          </Grid>
                        );
                      })}
                    </Grid>
                  );
                })}

              <Grid container alignItems="center" sx={{ mt: 1 }}>
                <Grid item sx={{ flex: 1 }}>
                  <Typography sx={{ fontWeight: 600, ...FONT_STYLE }}>
                    Total points
                    {group.averageFactor !== 1
                      ? ` (sum * ${group.averageFactor})`
                      : ""}
                  </Typography>
                </Grid>

                {totalsByGroup[gi]?.map((t, idx) => (
                  <Grid
                    item
                    key={idx}
                    sx={{ width: 90, textAlign: "center", ml: 1 }}
                  >
                    <Typography sx={FONT_STYLE}>{t.toFixed(2)}</Typography>
                  </Grid>
                ))}
              </Grid>
              {phase === EventPhase.Interview && (
                <Box sx={{ mt: 2 }}>
                  <TextField
                    multiline
                    rows={3}
                    fullWidth
                    size="small"
                    placeholder="Remarks..."
                    value={remarksByGroup[group.id] ?? ""}
                    onChange={(e) =>
                      setRemarksByGroup((prev) => ({
                        ...prev,
                        [group.id]: e.target.value,
                      }))
                    }
                    inputProps={{
                      style: {
                        ...FONT_STYLE,
                        resize: "vertical",
                      },
                    }}
                    disabled={allReadonly}
                  />
                </Box>
              )}
            </Box>
          );
        })}

      {phase === EventPhase.Interview && (
        <Box sx={{ textAlign: "right", mt: 1 }}>
          <Button
            size="small"
            variant="contained"
            onClick={() => handleSaveInterview(remarksByGroup)}
            disabled={allReadonly}
            sx={{ textTransform: "none", ...FONT_STYLE }}
          >
            Save
          </Button>
        </Box>
      )}

      <Divider sx={{ mt: 2 }} />
    </Box>
  );
};

export default InterviewPanel;
